import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFilaments, getPrinters, getAccessories, getRecentSales, calculatePrint, createSale, import3mf, getClients, generateQuotePdf, getCricutProjectsFor3D, getEquipment } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Calculator, Receipt, Save, AlertCircle, Package, Plus, Minus, Trash2, Scissors, 
  Palette, Copy, History, Euro, Percent, Clock, Upload, FileText, Download, Sparkles, Printer
} from 'lucide-react';
import { AffiliateLinks } from '../components/AffiliateLinks';
import { toast } from 'sonner';
import { FilamentColorDot } from '../components/FilamentColorDot';
import { filamentName } from '../lib/filamentVariants';
import { DecimalInput } from '../components/DecimalInput';
import { NumberSpinner } from '../components/NumberSpinner';
import { downloadHtmlAsPdf } from '../lib/pdfExport';

// Helper functions for time conversion
const hoursToHM = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return { hours: h, minutes: m };
};

const hmToHours = (h, m) => {
  return (parseInt(h) || 0) + (parseInt(m) || 0) / 60;
};

const formatTime = (hours) => {
  const { hours: h, minutes: m } = hoursToHM(hours);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

export default function CalculatorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filaments, setFilaments] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [cricutProjects, setCricutProjects] = useState([]);
  const [selectedCricutIds, setSelectedCricutIds] = useState([]); // preventivi Cricut aggiunti come lavorazione
  const [applyMarginToExtras, setApplyMarginToExtras] = useState(true); // se true, il costo Cricut viene moltiplicato per il margine
  const [equipmentList, setEquipmentList] = useState([]); // attrezzature 3D disponibili
  const [selectedEquipment, setSelectedEquipment] = useState([]); // [{equipment_id, hours}]
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [useManualPrice, setUseManualPrice] = useState(false);
  const [clients, setClientsList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [quotePreview, setQuotePreview] = useState('');
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  const [generatingQuote, setGeneratingQuote] = useState(false);
  const [calcQuoteNumber, setCalcQuoteNumber] = useState('');
  const [downloadingCalcPdf, setDownloadingCalcPdf] = useState(false);

  // Separate state for hours and minutes
  const [printTimeH, setPrintTimeH] = useState(2);
  const [printTimeM, setPrintTimeM] = useState(0);
  const [designTimeH, setDesignTimeH] = useState(0);
  const [designTimeM, setDesignTimeM] = useState(0);
  const skipTimeEffect = useRef(false);
  const file3mfRef = useRef(null);
  const [importing3mf, setImporting3mf] = useState(false);

  const [formData, setFormData] = useState({
    filaments: [],
    printer_id: '',
    print_time_hours: 2,
    design_hours: 0,
    margin_percent: 30,
    manual_price: null,
    quantity: 1,
    product_name: '',
    accessories: [],
    // Pro features
    yield_rate: 100,
    maintenance_cost_per_hour: null,
    vat_rate: 0,
  });

  const [result, setResult] = useState(null);

  // Update formData when time fields change (skip during copy)
  useEffect(() => {
    if (skipTimeEffect.current) {
      skipTimeEffect.current = false;
      return;
    }
    setFormData(prev => ({
      ...prev,
      print_time_hours: hmToHours(printTimeH, printTimeM),
      labor_hours: 0,
      design_hours: hmToHours(designTimeH, designTimeM)
    }));
  }, [printTimeH, printTimeM, designTimeH, designTimeM]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [filamentsData, printersData, accessoriesData, recentData, clientsData, cricutData, equipmentData] = await Promise.all([
        getFilaments(),
        getPrinters(),
        getAccessories(),
        getRecentSales(10),
        getClients(),
        getCricutProjectsFor3D().catch(() => []),
        getEquipment().catch(() => [])
      ]);
      setFilaments(filamentsData);
      setPrinters(printersData);
      setAccessories(accessoriesData);
      setRecentSales(recentData);
      setClientsList(clientsData);
      setCricutProjects(cricutData);
      setEquipmentList(equipmentData);
      
      if (printersData.length > 0) {
        setFormData(prev => ({ ...prev, printer_id: printersData[0].id }));
      }
      if (filamentsData.length > 0) {
        setFormData(prev => ({ 
          ...prev, 
          filaments: [{ filament_id: filamentsData[0].id, grams_used: 50 }]
        }));
      }
      
      // Handle reprint from Sales page
      const reprintParam = searchParams.get('reprint');
      if (reprintParam) {
        try {
          const rp = JSON.parse(reprintParam);
          skipTimeEffect.current = true;
          
          const validFilaments = (rp.filaments || []).filter(f => 
            filamentsData.some(fd => fd.id === f.filament_id)
          );
          const validPrinter = printersData.some(p => p.id === rp.printer_id);
          const validAccessories = (rp.accessories || []).filter(a =>
            accessoriesData.some(acc => acc.id === a.accessory_id)
          );
          
          const printTime = hoursToHM(rp.print_time_hours || 2);
          setPrintTimeH(printTime.hours);
          setPrintTimeM(printTime.minutes);
          const designTime = hoursToHM(rp.design_hours || 0);
          setDesignTimeH(designTime.hours);
          setDesignTimeM(designTime.minutes);
          
          setFormData(prev => ({
            ...prev,
            filaments: validFilaments.length > 0 ? validFilaments : prev.filaments,
            printer_id: validPrinter ? rp.printer_id : prev.printer_id,
            print_time_hours: rp.print_time_hours || 2,
            design_hours: rp.design_hours || 0,
            accessories: validAccessories,
            product_name: rp.product_name || '',
          }));
          
          // Clean URL
          setSearchParams({});
          toast.success(`Ristampa "${rp.product_name}" caricata`);
        } catch (e) { /* ignore parse errors */ }
      }
    } catch (err) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handle3mfImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting3mf(true);
    try {
      const data = await import3mf(file);
      if (data.total_time_seconds > 0) {
        const totalH = Math.floor(data.total_time_seconds / 3600);
        const totalM = Math.round((data.total_time_seconds % 3600) / 60);
        skipTimeEffect.current = true;
        setPrintTimeH(totalH);
        setPrintTimeM(totalM);
        setFormData(prev => ({
          ...prev,
          print_time_hours: data.total_time_hours || hmToHours(totalH, totalM),
        }));
      }
      
      // Handle filament details from 3mf
      const plateDetails = data.plates?.[0]?.filament_details || [];
      
      if (plateDetails.length > 0 && filaments.length > 0) {
        // Match 3mf filaments to user's inventory by color hex, avoiding duplicates
        const usedIds = new Set();
        
        const newFilaments = plateDetails.map(detail => {
          // Try exact color hex match (not already used)
          const colorMatch = detail.color ? filaments.find(f => 
            !usedIds.has(f.id) && (
              f.color_hex?.toUpperCase() === detail.color?.toUpperCase() ||
              f.color_hex2?.toUpperCase() === detail.color?.toUpperCase()
            )
          ) : null;
          
          // Try material type match (not already used)
          const typeMatch = !colorMatch && detail.type ? filaments.find(f =>
            !usedIds.has(f.id) && f.material_type?.toUpperCase() === detail.type?.toUpperCase()
          ) : null;
          
          // Fallback: any filament not yet used, or first filament
          const anyMatch = !colorMatch && !typeMatch ? 
            (filaments.find(f => !usedIds.has(f.id)) || filaments[0]) : null;
          
          const matched = colorMatch || typeMatch || anyMatch;
          usedIds.add(matched.id);
          
          return {
            filament_id: matched.id,
            grams_used: detail.grams || 0
          };
        });
        
        setFormData(prev => ({ ...prev, filaments: newFilaments }));
      } else if (data.total_filament_grams > 0 && formData.filaments.length > 0) {
        // Fallback: put all grams in first filament
        setFormData(prev => ({
          ...prev,
          filaments: prev.filaments.map((f, i) => i === 0 ? { ...f, grams_used: data.total_filament_grams } : f)
        }));
      }
      
      const filCount = plateDetails.length || 1;
      const filMsg = filCount > 1 ? `, ${filCount} filamenti` : '';
      toast.success(`Importato: ${formatTime(data.total_time_hours)}h, ${data.total_filament_grams}g filamento${filMsg}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nell\'importazione del file .3mf');
    } finally {
      setImporting3mf(false);
      if (file3mfRef.current) file3mfRef.current.value = '';
    }
  };

  const handleCalculate = useCallback(async () => {
    if (formData.filaments.length === 0 || !formData.printer_id) return;
    
    setCalculating(true);
    try {
      // Calcola costo extra dai preventivi Cricut selezionati
      // - Se applichiamo margine: usa total_cost (costo di produzione) -> il margine si applica
      // - Se non applichiamo margine: usa recommended_price (quello che si vuole incassare) -> pass-through
      const cricutSum = selectedCricutIds.reduce((s, id) => {
        const cp = cricutProjects.find(p => p.id === id);
        if (!cp) return s;
        const val = applyMarginToExtras ? (cp.total_cost || 0) : (cp.recommended_price || cp.total_cost || 0);
        return s + val;
      }, 0);
      const calcData = {
        ...formData,
        equipment: selectedEquipment.filter(e => e.equipment_id && Number(e.hours) > 0),
        extra_costs: cricutSum,
        apply_margin_to_extras: applyMarginToExtras,
        manual_price: useManualPrice ? formData.manual_price : null
      };
      const data = await calculatePrint(calcData);
      setResult(data);
    } catch (err) {
      console.error('Calculation error:', err);
    } finally {
      setCalculating(false);
    }
  }, [formData, useManualPrice, selectedCricutIds, cricutProjects, applyMarginToExtras, selectedEquipment]);

  useEffect(() => {
    if (formData.filaments.length > 0 && formData.printer_id) {
      const timer = setTimeout(handleCalculate, 300);
      return () => clearTimeout(timer);
    }
  }, [formData, useManualPrice, handleCalculate, selectedCricutIds, applyMarginToExtras, selectedEquipment]);

  // Copy from previous sale
  const copyFromSale = (sale) => {
    // Handle filaments - support both new format (array) and legacy
    let saleFilaments = sale.filaments || [];
    if (saleFilaments.length === 0 && sale.filament_id) {
      saleFilaments = [{ filament_id: sale.filament_id, grams_used: sale.grams_used || 50 }];
    }
    
    const validFilaments = saleFilaments.filter(sf => 
      filaments.some(f => f.id === sf.filament_id)
    );
    
    let saleAccessories = sale.accessories || [];
    const validAccessories = saleAccessories.filter(sa =>
      accessories.some(a => a.id === sa.accessory_id)
    );
    
    const printerExists = printers.some(p => p.id === sale.printer_id);
    
    // Skip the time useEffect to avoid race condition
    skipTimeEffect.current = true;
    
    // Update time display
    const printTime = hoursToHM(sale.print_time_hours || 2);
    setPrintTimeH(printTime.hours);
    setPrintTimeM(printTime.minutes);
    const designTime = hoursToHM(sale.design_hours || 0);
    setDesignTimeH(designTime.hours);
    setDesignTimeM(designTime.minutes);
    
    // Set formData with all values at once
    setFormData({
      filaments: validFilaments.length > 0 ? validFilaments : formData.filaments,
      printer_id: printerExists ? sale.printer_id : formData.printer_id,
      print_time_hours: sale.print_time_hours || 2,
      labor_hours: 0,
      design_hours: sale.design_hours || 0,
      quantity: sale.quantity || 1,
      accessories: validAccessories,
      product_name: sale.product_name || '',
      margin_percent: formData.margin_percent,
      manual_price: null
    });
    
    setUseManualPrice(false);
    setResult(null);
    
    toast.success(`Configurazione "${sale.product_name}" caricata`);
  };

  // Filament management
  const addFilament = () => {
    if (filaments.length === 0) return;
    const usedIds = formData.filaments.map(f => f.filament_id);
    const available = filaments.find(f => !usedIds.includes(f.id)) || filaments[0];
    setFormData(prev => ({
      ...prev,
      filaments: [...prev.filaments, { filament_id: available.id, grams_used: 30 }]
    }));
  };

  const removeFilament = (index) => {
    if (formData.filaments.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      filaments: prev.filaments.filter((_, i) => i !== index)
    }));
  };

  const updateFilament = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      filaments: prev.filaments.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  // Accessory management
  const handleAccessoryToggle = (accessoryId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        accessories: [...prev.accessories, { accessory_id: accessoryId, quantity: 1 }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        accessories: prev.accessories.filter(a => a.accessory_id !== accessoryId)
      }));
    }
  };

  const handleAccessoryQuantity = (accessoryId, delta) => {
    setFormData(prev => ({
      ...prev,
      accessories: prev.accessories.map(a => {
        if (a.accessory_id === accessoryId) {
          return { ...a, quantity: Math.max(1, a.quantity + delta) };
        }
        return a;
      })
    }));
  };

  const getAccessoryUsage = (accessoryId) => {
    return formData.accessories.find(a => a.accessory_id === accessoryId);
  };

  const handleSaveSale = async () => {
    if (!formData.product_name) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }
    if (!result) {
      toast.error('Calcola prima i costi');
      return;
    }

    setSaving(true);
    try {
      const res = await createSale({
        date: new Date().toISOString().split('T')[0],
        product_name: formData.product_name,
        filaments: formData.filaments,
        printer_id: formData.printer_id,
        print_time_hours: formData.print_time_hours,
        labor_hours: 0,
        design_hours: formData.design_hours,
        quantity: formData.quantity,
        sale_price: result.sale_price_total,
        accessories: formData.accessories,
        client_id: selectedClientId && selectedClientId !== 'none' ? selectedClientId : null,
        source_module: '3d',
        cricut_project_id: selectedCricutIds[0] || null, // se piu' preventivi, tiene il primo per link
      });
      const count = res.count || 1;
      toast.success(count > 1 ? `${count} vendite registrate!` : 'Vendita registrata!');
      setFormData(prev => ({ ...prev, product_name: '' }));
      loadData();
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const selectedPrinter = printers.find(p => p.id === formData.printer_id);

  const handleGenerateQuote = async () => {
    if (!formData.product_name || !result) return;
    setGeneratingQuote(true);
    try {
      const clientId = selectedClientId && selectedClientId !== 'none' ? selectedClientId : null;
      const selectedClient = clientId ? clients.find(c => c.id === clientId) : null;
      const items = [{
        description: formData.product_name,
        quantity: formData.quantity,
        unit_price: result.sale_price_per_unit
      }];
      const res = await generateQuotePdf({
        client_id: clientId,
        client_name: selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : '',
        items,
        notes: '',
        valid_days: 30
      });
      setQuotePreview(res.html);
      setCalcQuoteNumber(res.quote_number || '');
      setShowQuotePreview(true);
      toast.success(`Preventivo ${res.quote_number} generato`);
    } catch {
      toast.error('Errore generazione preventivo');
    } finally {
      setGeneratingQuote(false);
    }
  };

  const handlePrintQuote = () => {
    const win = window.open('', '_blank');
    win.document.write(quotePreview);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const handleDownloadCalcPdf = async () => {
    if (!quotePreview) return;
    setDownloadingCalcPdf(true);
    try {
      const filename = `Preventivo_${calcQuoteNumber || 'documento'}.pdf`;
      await downloadHtmlAsPdf(quotePreview, filename);
      toast.success('PDF scaricato');
    } catch {
      toast.error('Errore nel download del PDF');
    } finally {
      setDownloadingCalcPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="calculator-loading">
        <div className="text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  if (filaments.length === 0 || printers.length === 0) {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="calculator-setup-required">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Calcolatore Costi</h1>
          <p className="text-muted-foreground mt-1">Calcola i costi di stampa in tempo reale</p>
        </div>
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
            <h3 className="font-heading font-semibold mb-2">Configurazione richiesta</h3>
            <p className="text-muted-foreground mb-4">
              {filaments.length === 0 && 'Aggiungi almeno un filamento. '}
              {printers.length === 0 && 'Aggiungi almeno una stampante nelle impostazioni.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="calculator-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Calcolatore Costi</h1>
          <p className="text-muted-foreground mt-1">Multicolore • Quantità multiple • Prezzo manuale</p>
        </div>
        <div>
          <input ref={file3mfRef} type="file" accept=".3mf" className="hidden" onChange={handle3mfImport} />
          <Button variant="outline" size="sm" onClick={() => file3mfRef.current?.click()} disabled={importing3mf} data-testid="import-3mf-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {importing3mf ? 'Importazione...' : 'Importa .3mf'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Sales - Copy Feature */}
        <Card className="border-border/40 lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-heading">
              <History className="w-4 h-4" />
              Stampe Recenti
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[400px]">
              {recentSales.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  Nessuna stampa precedente
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {recentSales.map(sale => (
                    <div 
                      key={sale.id}
                      className="p-3 rounded-sm hover:bg-muted/50 cursor-pointer group transition-colors"
                      onClick={() => copyFromSale(sale)}
                      data-testid={`copy-sale-${sale.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{sale.product_name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {sale.material_type} • {sale.grams_used}g • {formatTime(sale.print_time_hours || 0)}
                          </p>
                          <p className="text-xs font-mono text-primary">
                            €{sale.sale_price?.toFixed(2)} {sale.quantity > 1 && `(x${sale.quantity})`}
                          </p>
                        </div>
                        <Copy className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Input Form */}
        <Card className="border-border/40 lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-heading">
              <Calculator className="w-4 h-4" />
              Parametri Stampa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filaments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-xs">
                  <Palette className="w-3 h-3" />
                  Filamenti {formData.filaments.length > 1 && <span className="bg-primary/20 text-primary px-1.5 py-0.5 rounded-full text-[10px]">Multi</span>}
                </Label>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={addFilament}>
                  <Plus className="w-3 h-3 mr-1" />Colore
                </Button>
              </div>
              <div className="space-y-1.5">
                {formData.filaments.map((f, index) => {
                  const filamentData = filaments.find(fil => fil.id === f.filament_id);
                  return (
                    <div key={index} className="flex items-center gap-1.5 p-1.5 rounded-sm bg-muted/30 border border-border/40">
                      <FilamentColorDot color={filamentData?.color_hex || '#FFF'} color2={filamentData?.color_hex2} finish={filamentData?.finish} size="w-5 h-5" />
                      <Select value={f.filament_id} onValueChange={(v) => updateFilament(index, 'filament_id', v)}>
                        <SelectTrigger className="flex-1 h-7 text-xs" data-testid={`filament-select-${index}`} title={filamentData ? `${filamentName(filamentData)} ${filamentData.color}${filamentData.brand ? ` - ${filamentData.brand}` : ''}` : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {filaments.map(fil => (
                            <SelectItem key={fil.id} value={fil.id}>
                              <div className="flex items-center gap-1.5">
                                <FilamentColorDot color={fil.color_hex} color2={fil.color_hex2} finish={fil.finish} size="w-3 h-3" />
                                <span>{filamentName(fil)} {fil.color}{fil.brand ? ` - ${fil.brand}` : ''}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <DecimalInput
                        value={f.grams_used}
                        onChange={(num) => updateFilament(index, 'grams_used', num)}
                        className="w-16 h-7 font-mono text-xs"
                        data-testid={`grams-input-${index}`}
                      />
                      <span className="text-[10px] text-muted-foreground">g</span>
                      {formData.filaments.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFilament(index)}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Printer */}
            <div className="space-y-1">
              <Label className="text-xs">Stampante</Label>
              <Select value={formData.printer_id} onValueChange={(v) => setFormData({...formData, printer_id: v})}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {printers.map(p => <SelectItem key={p.id} value={p.id}>{p.printer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Print Time - Hours and Minutes */}
            <div className="space-y-1">
              <Label className="flex items-center gap-2 text-xs">
                <Clock className="w-3 h-3" />
                Tempo Stampa
              </Label>
              <div className="flex items-center gap-1">
                <NumberSpinner
                  min={0}
                  value={printTimeH}
                  onChange={(num) => setPrintTimeH(parseInt(num) || 0)}
                  className="h-8 w-16 font-mono text-xs text-center"
                  data-testid="print-time-h"
                />
                <span className="text-xs text-muted-foreground">h</span>
                <NumberSpinner
                  min={0}
                  max={59}
                  value={printTimeM}
                  onChange={(num) => setPrintTimeM(Math.min(59, parseInt(num) || 0))}
                  className="h-8 w-16 font-mono text-xs text-center"
                  data-testid="print-time-m"
                />
                <span className="text-xs text-muted-foreground">m</span>
              </div>
            </div>

            {/* Design + Quantity */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Design</Label>
                <div className="flex items-center gap-1">
                  <NumberSpinner
                    min={0}
                    value={designTimeH}
                    onChange={(num) => setDesignTimeH(parseInt(num) || 0)}
                    className="h-8 w-14 font-mono text-xs text-center"
                    data-testid="design-time-h"
                  />
                  <span className="text-xs text-muted-foreground">h</span>
                  <NumberSpinner
                    min={0}
                    max={59}
                    value={designTimeM}
                    onChange={(num) => setDesignTimeM(Math.min(59, parseInt(num) || 0))}
                    className="h-8 w-14 font-mono text-xs text-center"
                    data-testid="design-time-m"
                  />
                  <span className="text-xs text-muted-foreground">m</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Quantità</Label>
                <NumberSpinner
                  min={1}
                  value={formData.quantity}
                  onChange={(num) => setFormData({...formData, quantity: parseInt(num) || 1})}
                  className="h-8 font-mono text-xs"
                  data-testid="quantity-input"
                />
              </div>
            </div>

            {/* Pro features: yield rate, manutenzione, IVA */}
            <div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-primary flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Calcolo Professionale
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground" title="Percentuale di stampe andate a buon fine. Es. 85% = 15% delle stampe falliscono">Tasso Successo (%)</Label>
                  <NumberSpinner
                    min={1}
                    max={100}
                    step={1}
                    value={formData.yield_rate}
                    onChange={(num) => setFormData({...formData, yield_rate: parseFloat(num) || 100})}
                    className="h-8 font-mono text-xs"
                    placeholder="100"
                    data-testid="yield-rate-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground" title="Costo orario manutenzione/riparazioni (es. ugelli, cinghie, lubrificanti). Lascia vuoto per usare quello della stampante.">Manutenzione (€/h)</Label>
                  <DecimalInput
                    value={formData.maintenance_cost_per_hour == null ? '' : formData.maintenance_cost_per_hour}
                    onChange={(num) => setFormData({...formData, maintenance_cost_per_hour: isNaN(num) ? null : num})}
                    className="h-8 font-mono text-xs"
                    placeholder="auto"
                    data-testid="maintenance-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground" title="Aliquota IVA da applicare al prezzo finale (es. 22 per IVA 22%). 0 = nessuna IVA.">IVA (%)</Label>
                  <NumberSpinner
                    min={0}
                    max={100}
                    step={1}
                    value={formData.vat_rate}
                    onChange={(num) => setFormData({...formData, vat_rate: parseFloat(num) || 0})}
                    className="h-8 font-mono text-xs"
                    placeholder="0"
                    data-testid="vat-rate-input"
                  />
                </div>
              </div>
            </div>

            {/* Accessories */}
            {accessories.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-xs"><Package className="w-3 h-3" />Accessori</Label>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {accessories.map(acc => {
                    const usage = getAccessoryUsage(acc.id);
                    return (
                      <div key={acc.id} className="flex items-center justify-between p-1.5 rounded-sm bg-muted/30 border border-border/40">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={!!usage} onCheckedChange={(c) => handleAccessoryToggle(acc.id, c)} />
                          <span className="text-xs">{acc.name}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">€{acc.unit_cost.toFixed(2)}</span>
                        </div>
                        {usage && (
                          <div className="flex items-center gap-0.5">
                            <Button type="button" size="icon" variant="ghost" className="h-5 w-5" onClick={() => handleAccessoryQuantity(acc.id, -1)}>
                              <Minus className="w-2.5 h-2.5" />
                            </Button>
                            <span className="font-mono text-xs w-5 text-center">{usage.quantity}</span>
                            <Button type="button" size="icon" variant="ghost" className="h-5 w-5" onClick={() => handleAccessoryQuantity(acc.id, 1)}>
                              <Plus className="w-2.5 h-2.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Attrezzature 3D (AMS, ecc.) */}
            {equipmentList.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-xs"><Sparkles className="w-3 h-3" />Attrezzature (AMS, piatti, ecc.)</Label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {equipmentList.map(eq => {
                    const sel = selectedEquipment.find(e => e.equipment_id === eq.id);
                    const checked = !!sel;
                    return (
                      <div key={eq.id} className="flex items-center justify-between gap-2 p-1.5 rounded-sm bg-muted/30 border border-border/40">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(c) => {
                              setSelectedEquipment(prev => c
                                ? [...prev, { equipment_id: eq.id, hours: formData.print_time_hours || 0 }]
                                : prev.filter(x => x.equipment_id !== eq.id));
                            }}
                            data-testid={`equipment-check-${eq.id}`}
                          />
                          <span className="text-xs truncate" title={eq.name}>{eq.name}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">€{eq.amortization_per_hour.toFixed(4)}/h</span>
                        </div>
                        {checked && (
                          <Input
                            type="number"
                            step="0.25"
                            className="h-6 w-16 text-xs"
                            value={sel.hours}
                            onChange={(e) => {
                              const h = Number(e.target.value) || 0;
                              setSelectedEquipment(prev => prev.map(x => x.equipment_id === eq.id ? { ...x, hours: h } : x));
                            }}
                            data-testid={`equipment-hours-${eq.id}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground italic">Attrezzature amortizzate a tempo (es. Bambu Lab AMS). Gestisci l'elenco dalle Impostazioni.</p>
              </div>
            )}

            {/* Lavorazioni Cricut */}
            {cricutProjects.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-xs"><Scissors className="w-3 h-3" />Lavorazioni Cricut</Label>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {cricutProjects.map(cp => {
                    const checked = selectedCricutIds.includes(cp.id);
                    return (
                      <div key={cp.id} className="flex items-center justify-between p-1.5 rounded-sm bg-muted/30 border border-border/40">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(c) => setSelectedCricutIds(prev => c ? [...prev, cp.id] : prev.filter(x => x !== cp.id))}
                            data-testid={`cricut-check-${cp.id}`}
                          />
                          <span className="text-xs truncate max-w-[130px]" title={cp.name}>{cp.name}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">€{cp.total_cost.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedCricutIds.length > 0 && (
                  <div className="flex items-center gap-2 p-2 rounded bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900">
                    <Checkbox
                      id="apply-margin-cricut"
                      checked={applyMarginToExtras}
                      onCheckedChange={setApplyMarginToExtras}
                      data-testid="apply-margin-cricut-check"
                    />
                    <label htmlFor="apply-margin-cricut" className="text-[11px] cursor-pointer flex-1">
                      Applica il margine anche al costo Cricut <span className="text-muted-foreground">(consigliato per guadagnare anche sulle lavorazioni)</span>
                    </label>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground italic">Da &quot;Cricut&quot; spunta <b>Aggiungi al calcolatore Stampa 3D</b> per farle apparire qui.</p>
              </div>
            )}

            {/* Price Mode Toggle */}
            <div className="space-y-3 pt-2 border-t border-border/40">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-xs">
                  {useManualPrice ? <Euro className="w-3 h-3" /> : <Percent className="w-3 h-3" />}
                  {useManualPrice ? 'Prezzo Manuale' : 'Margine %'}
                </Label>
                <Switch checked={useManualPrice} onCheckedChange={setUseManualPrice} data-testid="price-mode-toggle" />
              </div>
              
              {useManualPrice ? (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Prezzo per unità (€)</Label>
                  <DecimalInput
                    value={formData.manual_price || ''}
                    onChange={(num) => setFormData(prev => ({...prev, manual_price: num > 0 ? num : null}))}
                    placeholder="Es. 15.00"
                    className="h-8 font-mono text-xs"
                    data-testid="manual-price-input"
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Margine</span>
                    <span className="font-mono text-primary font-semibold">{formData.margin_percent}%</span>
                  </div>
                  <Slider
                    value={[formData.margin_percent]}
                    onValueChange={(v) => setFormData({...formData, margin_percent: v[0]})}
                    min={0} max={200} step={5}
                  />
                </div>
              )}
            </div>

            {/* Product Name + Client */}
            <div className="space-y-1">
              <Label className="text-xs">Nome Prodotto</Label>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                placeholder="Es. Portachiavi logo"
                className="h-8 text-xs"
                data-testid="calc-product-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cliente (opzionale)</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger className="h-8 text-xs" data-testid="calc-client-select">
                  <SelectValue placeholder="Nessun cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun cliente</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} {c.surname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Receipt / Results */}
        <Card className="border-border/40 lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-heading">
              <Receipt className="w-4 h-4" />
              Riepilogo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {calculating ? (
              <div className="text-center py-8 text-muted-foreground text-sm">Calcolo...</div>
            ) : result ? (
              <>
                {/* Filaments */}
                <div className="space-y-1">
                  {result.filaments_details?.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <FilamentColorDot color={f.color_hex} color2={f.color_hex2} finish={f.finish} size="w-3 h-3" />
                      <span className="flex-1">{filamentName(f)} {f.color}{f.brand ? ` - ${f.brand}` : ''}</span>
                      <span className="font-mono text-muted-foreground">{f.grams_used}g</span>
                      <span className="font-mono">€{f.total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Separator className="border-dashed" />

                {/* Costs breakdown */}
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span>Materiale ({result.total_grams}g)</span><span className="font-mono">€{result.material_cost.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Elettricità</span><span className="font-mono">€{result.electricity_cost.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Ammortamento</span><span className="font-mono">€{result.depreciation_cost.toFixed(2)}</span></div>
                  {result.maintenance_cost > 0 && <div className="flex justify-between"><span>Manutenzione</span><span className="font-mono">€{result.maintenance_cost.toFixed(2)}</span></div>}
                  {result.accessories_cost > 0 && <div className="flex justify-between"><span>Accessori</span><span className="font-mono">€{result.accessories_cost.toFixed(2)}</span></div>}
                  {result.equipment_cost > 0 && (
                    <div className="flex justify-between text-blue-600 dark:text-blue-400">
                      <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />Attrezzature</span>
                      <span className="font-mono">€{result.equipment_cost.toFixed(2)}</span>
                    </div>
                  )}
                  {result.labor_cost > 0 && <div className="flex justify-between"><span>Lavoro</span><span className="font-mono">€{result.labor_cost.toFixed(2)}</span></div>}
                  {result.design_cost > 0 && <div className="flex justify-between"><span>Design</span><span className="font-mono">€{result.design_cost.toFixed(2)}</span></div>}
                  {result.yield_extra_cost > 0 && (
                    <div className="flex justify-between text-amber-600 dark:text-amber-400" title={`Tasso successo ${result.yield_rate}%: assorbe il costo delle stampe fallite`}>
                      <span>Rischio fallimento ({(100 - result.yield_rate).toFixed(0)}%)</span>
                      <span className="font-mono">+€{result.yield_extra_cost.toFixed(2)}</span>
                    </div>
                  )}
                  {result.extra_costs > 0 && (
                    <div className="flex justify-between text-orange-600 dark:text-orange-400">
                      <span className="inline-flex items-center gap-1"><Scissors className="w-3 h-3" />Lavorazioni Cricut</span>
                      <span className="font-mono">€{result.extra_costs.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Totals */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between font-medium">
                    <span>Costo Totale</span>
                    <span className="font-mono">€{result.total_cost.toFixed(2)}</span>
                  </div>
                  {result.quantity > 1 && (
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Costo/unità</span>
                      <span className="font-mono">€{result.cost_per_unit.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Sale Price */}
                <div className="pt-3 border-t-2 border-primary space-y-2">
                  {result.quantity > 1 ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span>Prezzo/unità</span>
                        <span className="font-mono text-primary font-semibold">€{result.sale_price_per_unit.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-lg font-heading font-bold">TOTALE ({result.quantity} pz)</span>
                        <span className="text-xl font-mono font-bold text-primary">€{result.sale_price_total.toFixed(2)}</span>
                      </div>
                      {result.vat_rate > 0 && (
                        <>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>+ IVA {result.vat_rate.toFixed(0)}%</span>
                            <span className="font-mono">€{result.vat_amount_total.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-semibold pt-1 border-t border-border/40">
                            <span>TOTALE c/IVA</span>
                            <span className="font-mono text-primary">€{result.price_with_vat_total.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Profitto/unità</span>
                        <span className="font-mono text-emerald-500">+€{result.net_profit_per_unit.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Profitto Totale</span>
                        <span className="font-mono font-semibold text-emerald-500">+€{result.net_profit_total.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-heading font-bold">PREZZO</span>
                        <span className="text-2xl font-mono font-bold text-primary">€{result.sale_price_per_unit.toFixed(2)}</span>
                      </div>
                      {result.vat_rate > 0 && (
                        <>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>+ IVA {result.vat_rate.toFixed(0)}%</span>
                            <span className="font-mono">€{result.vat_amount_per_unit.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-semibold pt-1 border-t border-border/40">
                            <span>Prezzo c/IVA</span>
                            <span className="font-mono text-primary">€{result.price_with_vat_per_unit.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Profitto ({result.margin_percent.toFixed(0)}%)</span>
                        <span className="font-mono font-semibold text-emerald-500">+€{result.net_profit_per_unit.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>

                <Button className="w-full mt-3" onClick={handleSaveSale} disabled={saving || !formData.product_name} data-testid="save-sale-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Salvataggio...' : 'Registra Vendita'}
                </Button>
                <Button variant="outline" className="w-full mt-1.5" onClick={handleGenerateQuote} disabled={generatingQuote || !formData.product_name || !result} data-testid="generate-quote-from-calc">
                  <FileText className="w-4 h-4 mr-2" />
                  {generatingQuote ? 'Generazione...' : 'Genera Preventivo PDF'}
                </Button>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Configura i parametri per calcolare
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Risorse consigliate (link affiliati) */}
      <AffiliateLinks placement="calculator" />

      {/* Quote Preview Dialog */}
      <Dialog open={showQuotePreview} onOpenChange={setShowQuotePreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between flex-wrap gap-2">
              Anteprima Preventivo
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={handleDownloadCalcPdf} disabled={downloadingCalcPdf} data-testid="download-calc-quote-pdf-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {downloadingCalcPdf ? 'Generazione...' : 'Scarica PDF'}
                </Button>
                <Button size="sm" onClick={handlePrintQuote} data-testid="print-calc-quote-btn">
                  <Printer className="w-3.5 h-3.5 mr-1.5" /> Stampa
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="border rounded-md p-4 bg-white text-black" dangerouslySetInnerHTML={{ __html: quotePreview }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
